// =====================================================
// BUDGETS LIST PAGE - Listado de presupuestos
// Interfaz rediseñada: búsqueda ampliada + filtros integrados
// =====================================================
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Search, X, Calendar } from 'lucide-react';
import { budgetsApi } from '../../api/budgets';
import { BudgetTable } from '../../components/budgets/BudgetTable';
import { PatientSelectModal } from '../../components/budgets/PatientSelectModal';
import type { PaginatedBudgets, BudgetQueryParams, BudgetStatus } from '../../types/budget';

const STATUS_TABS: { value: BudgetStatus | ''; label: string; color: string; active: string }[] = [
  { value: '',          label: 'Todos',     color: 'text-gray-600 border-gray-200 hover:bg-gray-50',           active: 'bg-gray-900 text-white border-gray-900' },
  { value: 'BORRADOR',  label: 'Borrador',  color: 'text-gray-500 border-gray-200 hover:bg-gray-50',           active: 'bg-gray-100 text-gray-700 border-gray-300' },
  { value: 'ENVIADO',   label: 'Enviado',   color: 'text-blue-600 border-blue-100 hover:bg-blue-50',           active: 'bg-blue-600 text-white border-blue-600' },
  { value: 'ACEPTADO',  label: 'Aceptado',  color: 'text-green-600 border-green-100 hover:bg-green-50',        active: 'bg-green-600 text-white border-green-600' },
  { value: 'RECHAZADO', label: 'Rechazado', color: 'text-red-500 border-red-100 hover:bg-red-50',              active: 'bg-red-500 text-white border-red-500' },
];

export function BudgetsListPage() {
  const navigate = useNavigate();
  const searchRef = useRef<HTMLInputElement>(null);
  const [data, setData] = useState<PaginatedBudgets | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPatientModal, setShowPatientModal] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  // Filtros
  const [statusFilter, setStatusFilter] = useState<BudgetStatus | ''>('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [includeRectifications, setIncludeRectifications] = useState(false);

  // Paginación
  const [queryParams, setQueryParams] = useState<BudgetQueryParams>({
    page: 1, limit: 20, sortBy: 'createdAt', sortOrder: 'desc',
  });

  // Modal eliminar
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; budgetNumber: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadBudgets = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: BudgetQueryParams = {
        ...queryParams,
        ...(statusFilter && { status: statusFilter }),
        ...(dateFrom && { dateFrom }),
        ...(dateTo && { dateTo }),
        ...(includeRectifications ? { includeRectifications: true } : {}),
      };
      const result = await budgetsApi.getAll(params);
      setData(result);
    } catch (err: any) {
      setError(err.message || 'Error al cargar presupuestos');
    } finally {
      setIsLoading(false);
    }
  }, [queryParams, statusFilter, dateFrom, dateTo, includeRectifications]);

  useEffect(() => { loadBudgets(); }, [loadBudgets]);

  const handleSearch = () => {
    setQueryParams(p => ({ ...p, page: 1, search: searchInput.trim() || undefined }));
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setQueryParams(p => ({ ...p, page: 1, search: undefined }));
  };

  const handleStatusTab = (val: BudgetStatus | '') => {
    setStatusFilter(val);
    setQueryParams(p => ({ ...p, page: 1 }));
  };

  const handleDeleteBudget = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await budgetsApi.delete(deleteTarget.id);
      setDeleteTarget(null);
      loadBudgets();
    } catch { setDeleteTarget(null); }
    finally { setIsDeleting(false); }
  };

  const handlePatientSelected = (patientId: string) => {
    navigate(`/presupuestos/nuevo?patientId=${patientId}`);
  };

  const hasDateFilter = dateFrom || dateTo;
  const hasAnyFilter = statusFilter || hasDateFilter || includeRectifications || queryParams.search;

  return (
    <div className="space-y-4">

      {/* ── Cabecera ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Presupuestos</h1>
          <p className="text-sm text-gray-500 mt-0.5">Gestión de presupuestos de la sede</p>
        </div>
        <button
          onClick={() => setShowPatientModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#008DD0] text-white rounded-xl hover:bg-[#0079b3] transition-colors shadow-sm font-medium"
        >
          <Plus className="h-5 w-5" />
          <span className="hidden sm:inline">Nuevo presupuesto</span>
          <span className="sm:hidden">Nuevo</span>
        </button>
      </div>

      {/* ── Panel de filtros integrado ───────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">

        {/* Buscador */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              ref={searchRef}
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Nº presupuesto, DNI completo o tratamiento..."
              className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#008DD0]/20 focus:border-[#008DD0] transition-colors bg-gray-50 placeholder-gray-400"
            />
            {searchInput && (
              <button onClick={handleClearSearch} className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 transition-colors">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-[#008DD0] text-white text-sm font-medium rounded-lg hover:bg-[#0079b3] transition-colors shrink-0"
          >
            Buscar
          </button>
          <button
            onClick={loadBudgets}
            disabled={isLoading}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-40 shrink-0"
            title="Actualizar"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Tabs de estado */}
        <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-gray-100 overflow-x-auto scrollbar-none">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => handleStatusTab(tab.value)}
              className={`px-3.5 py-1.5 rounded-lg text-sm font-medium border transition-all whitespace-nowrap ${
                statusFilter === tab.value ? tab.active : tab.color
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filtros de fecha + rectificaciones */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5">

          {/* Fecha desde */}
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            <span className="text-xs text-gray-500 shrink-0">Desde</span>
            <input
              type="date"
              value={dateFrom}
              onChange={e => { setDateFrom(e.target.value); setQueryParams(p => ({ ...p, page: 1 })); }}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:ring-2 focus:ring-[#008DD0]/20 focus:border-[#008DD0] transition-colors bg-gray-50"
            />
          </div>

          {/* Fecha hasta */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500 shrink-0">Hasta</span>
            <input
              type="date"
              value={dateTo}
              onChange={e => { setDateTo(e.target.value); setQueryParams(p => ({ ...p, page: 1 })); }}
              className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-700 focus:ring-2 focus:ring-[#008DD0]/20 focus:border-[#008DD0] transition-colors bg-gray-50"
            />
          </div>

          {/* Separador */}
          {hasDateFilter && <div className="h-4 w-px bg-gray-200" />}

          {/* Toggle rectificaciones */}
          <button
            onClick={() => { setIncludeRectifications(p => !p); setQueryParams(p => ({ ...p, page: 1 })); }}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
              includeRectifications
                ? 'bg-amber-50 border-amber-200 text-amber-700'
                : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
            }`}
          >
            <span className={`w-2 h-2 rounded-full shrink-0 ${includeRectifications ? 'bg-amber-500' : 'bg-gray-300'}`} />
            Rectificaciones
          </button>

          {/* Limpiar filtros */}
          {hasAnyFilter && (
            <button
              onClick={() => {
                setStatusFilter('');
                setDateFrom('');
                setDateTo('');
                setIncludeRectifications(false);
                setSearchInput('');
                setQueryParams({ page: 1, limit: 20, sortBy: 'createdAt', sortOrder: 'desc' });
              }}
              className="ml-auto inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              Limpiar
            </button>
          )}
        </div>
      </div>

      {/* ── Error ───────────────────────────────────────────────────────── */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          {error}
        </div>
      )}

      {/* ── Tabla ───────────────────────────────────────────────────────── */}
      {data && (
        <BudgetTable
          data={data}
          isLoading={isLoading}
          onPageChange={page => setQueryParams(p => ({ ...p, page }))}
          onDelete={(id, budgetNumber) => setDeleteTarget({ id, budgetNumber })}
        />
      )}

      {/* ── Modal eliminar ───────────────────────────────────────────────── */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-xl shadow-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Eliminar presupuesto</h3>
            <p className="text-gray-600 mb-6">
              ¿Seguro que quieres eliminar el borrador{' '}
              <span className="font-mono font-semibold text-gray-800">{deleteTarget.budgetNumber}</span>?
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteTarget(null)} disabled={isDeleting}
                className="px-4 py-2 text-gray-700 bg-gray-100 font-medium rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50">
                Cancelar
              </button>
              <button onClick={handleDeleteBudget} disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-2">
                {isDeleting && <span className="animate-spin text-sm">⏳</span>}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal selección paciente ─────────────────────────────────────── */}
      <PatientSelectModal
        isOpen={showPatientModal}
        onClose={() => setShowPatientModal(false)}
        onSelect={handlePatientSelected}
      />
    </div>
  );
}
